<script setup lang="ts">
import { Comment, computed, Fragment, ref, useId, useSlots, type VNode } from 'vue'

import AppIcon from '@ui/app-icon/AppIcon.vue'

import type { AppInputEmits, AppInputModelValue, AppInputProps } from './types'

const props = withDefaults(defineProps<AppInputProps>(), {
  type: 'text',
  size: 'md',
  variant: 'grey',
  label: '',
  description: '',
  placeholder: '',
  name: '',
  autocomplete: '',
  prefix: '',
  leftIcon: '',
  rightIcon: '',
  disabled: false,
  error: false,
  readonly: false,
  block: false,
  maxLength: -1,
  required: false,
  ariaLabel: '',
})

const slots = useSlots()

const model = defineModel<AppInputModelValue>('modelValue', {
  default: '',
})

const emit = defineEmits<AppInputEmits>()

const elementRef = ref<HTMLInputElement | null>(null)
const inputId = useId()
const descriptionId = useId()

const classes = computed(() => {
  return [
    `size-${props.size}`,
    `variant-${props.variant}`,
    { disabled: props.disabled },
    { error: props.error },
    { block: props.block },
  ]
})

function hasRenderedNodes(nodes: VNode[]): boolean {
  return nodes.some(node => node.type !== Comment && (node.type !== Fragment || hasRenderedNodes(node.children as VNode[])))
}

function hasIcon(name: 'left-icon' | 'right-icon', icon: string) {
  return !!icon || hasRenderedNodes(slots[name]?.() ?? [])
}

defineExpose({
  elementRef,
})
</script>

<template>
  <div
    class="app-input"
    :class="classes"
    data-test-id="app-input"
  >
    <label
      v-if="label"
      :for="inputId"
      class="app-input-label font-group-title"
      data-test-id="app-input-label"
    >
      {{ label }}
    </label>

    <div class="app-input-body">
      <div
        v-if="hasIcon('left-icon', leftIcon)"
        class="app-input-left-icon"
        data-test-id="app-input-left-icon"
      >
        <slot name="left-icon">
          <app-icon :name="leftIcon" />
        </slot>
      </div>

      <div class="app-input-field-wrapper">
        <div
          v-if="prefix"
          class="app-input-prefix"
          data-test-id="app-input-prefix"
        >
          {{ prefix }}
        </div>

        <input
          :id="inputId"
          ref="elementRef"
          v-model="model"
          :type="type"
          class="app-input-field"
          data-test-id="app-input-field"
          :name="name || undefined"
          :autocomplete="autocomplete || undefined"
          :placeholder="placeholder"
          :disabled="disabled"
          :maxlength="maxLength > 0 ? maxLength : undefined"
          :readonly="readonly"
          :required="required"
          :aria-label="ariaLabel || undefined"
          :aria-describedby="description ? descriptionId : undefined"
          :aria-invalid="error || undefined"
          @keydown="emit('keydown', $event)"
          @input="emit('input', $event)"
        >
      </div>

      <div
        v-if="hasIcon('right-icon', rightIcon)"
        class="app-input-right-icon"
        data-test-id="app-input-right-icon"
      >
        <slot name="right-icon">
          <app-icon :name="rightIcon" />
        </slot>
      </div>
    </div>

    <div
      v-if="description"
      :id="descriptionId"
      class="app-input-description font-caption"
      data-test-id="app-input-description"
    >
      {{ description }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.app-input {
  --padding: 0 16px;
  --height: 44px;
  --text-size: 14px;
  --icon-size: 16px;
  --text-color: var(--g-neutral-2);
  --label-text-color: var(--g-neutral-2);
  --placeholder-text-color: var(--g-neutral-3);
  --description-text-color: var(--g-neutral-3);
  --prefix-color: var(--g-neutral-2);
  --background-color: var(--g-neutral-7);
  --outline-color: transparent;
  --border-width: 1.2px;
  --border-radius: 12px;
  --cursor: text;

  $root: &;

  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;

  ::placeholder {
    color: var(--placeholder-text-color);
  }

  &.block {
    width: 100%;
  }

  &-label {
    margin-bottom: 0;
    color: var(--label-text-color);
    font-weight: 600;
  }

  &-field-wrapper {
    display: flex;
    flex: 1;
    align-items: center;
    height: 100%;
    gap: 4px;
  }

  &-prefix {
    flex-shrink: 0;
    color: var(--prefix-color);
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }

  &-left-icon {
    display: flex;
    align-items: center;
    color: var(--prefix-color);
    font-size: var(--icon-size);
  }

  &-right-icon {
    display: flex;
    align-items: center;
    color: var(--prefix-color);
    font-size: var(--icon-size);
  }

  &-body {
    display: flex;
    align-items: center;
    height: var(--height);
    padding: var(--padding);
    gap: 16px;
    border: var(--border-width) solid var(--outline-color);
    border-radius: var(--border-radius);
    background-color: var(--background-color);
    cursor: var(--cursor);
  }

  &-field {
    flex: 1 1 auto;
    width: 100%;
    min-width: 0;
    height: 100%;
    border: none;
    background-color: transparent;
    overflow: hidden;
    outline: none;
    color: var(--text-color);
    font-size: var(--text-size);
    font-weight: 500;
    white-space: nowrap;
    cursor: var(--cursor);
    text-overflow: ellipsis;

    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      box-shadow: 0 0 0 1000px var(--background-color) inset;
      transition: background-color 9999s ease-in-out 0s;
      caret-color: var(--text-color);
      -webkit-text-fill-color: var(--text-color);
    }
  }

  &-description {
    color: var(--description-text-color);
    font-weight: 500;
  }

  &:where(.variant-grey) {
    --background-color: var(--g-neutral-7);
    --outline-color: transparent;
  }

  &:where(.variant-white) {
    --background-color: var(--g-neutral-8);
    --outline-color: var(--g-neutral-5);
  }

  &:where(.size-sm) {
    --height: 36px;
    --text-size: 14px;
    --icon-size: 16px;
    --padding: 0 12px;
  }

  &:where(.size-md) {
    --height: 44px;
    --text-size: 14px;
    --icon-size: 16px;
    --padding: 0 16px;
  }

  &:where(.size-lg) {
    --height: 52px;
    --text-size: 16px;
    --icon-size: 20px;
    --padding: 0 16px;
  }

  &:where(.disabled) {
    --text-color: var(--g-neutral-4);
    --label-text-color: var(--g-neutral-4);
    --placeholder-text-color: var(--g-neutral-4);
    --description-text-color: var(--g-neutral-4);
    --prefix-color: var(--g-neutral-4);
    --background-color: var(--g-neutral-6);
    --cursor: not-allowed;
  }

  &:where(.error) {
    --description-text-color: var(--s-red);
    --outline-color: var(--s-red);
  }

  &:where(:has(:focus)) {
    --outline-color: var(--g-neutral-2);
  }
}
</style>
